import { metresPerPixelAt } from '@obraplan/core/domain/takeoff/floor-plan-sheet';
import type { Container } from '@/app/container';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { renderizarPrancha, type Renderizador } from '@/features/takeoff/renderizar-prancha';
import { useLive } from '@/lib/use-live';

/**
 * A prancha do desenho da frente, por baixo da planta da execução (22/10/2026).
 *
 * Na Veneza, a Fundação não tem cômodo: as estacas e os blocos foram posicionados sobre o PDF
 * da prancha, e a planta da Execução por cômodo os mostrava num fundo em branco. Aqui a prancha
 * volta, com a mesma página e a mesma escala da bancada: o canto de cima, à esquerda, da página é
 * o (0, 0), e cada pixel vale o m/px da calibração convertido para a largura renderizada
 * (`metresPerPixelAt`) — é nessa conta que o ponto da peça foi gravado.
 *
 * Sem prancha, sem calibração, ou sem permissão para lê-la, não há fundo: a planta desenha como
 * antes. A página renderizada fica guardada por prancha e escala — abrir a tela cheia, fechar e
 * abrir de novo não refaz o PDF.
 */
export interface FundoDaPlanta {
  /** A página renderizada, como `data:`. */
  readonly image: string;
  /** Largura e altura da página, em metros. */
  readonly width: number;
  readonly height: number;
}

const renderizadas = new Map<string, Promise<FundoDaPlanta | undefined>>();

type CasosDaPrancha = Pick<Container, 'getProjectFloorPlanSheets' | 'getFloorPlanSheetFile'>;

export async function lerPranchaDaFrente(
  container: CasosDaPrancha,
  session: Parameters<Container['getProjectFloorPlanSheets']['execute']>[1],
  projectId: Parameters<Container['getProjectFloorPlanSheets']['execute']>[0],
  floorPlanId: string,
  renderizar: Renderizador = renderizarPrancha,
): Promise<FundoDaPlanta | undefined> {
  const pranchas = await container.getProjectFloorPlanSheets.execute(projectId, session);
  if (!pranchas.ok) return undefined;
  const prancha = pranchas.value.find((item) => item.floorPlanId === floorPlanId);
  const calibracao = prancha?.calibration;
  if (prancha === undefined || calibracao === undefined) return undefined;

  const chave = `${prancha.id}|${String(calibracao.metresPerPixel)}|${String(calibracao.renderWidth)}`;
  const guardada = renderizadas.get(chave);
  if (guardada !== undefined) return guardada;

  const pagina = (async (): Promise<FundoDaPlanta | undefined> => {
    const url = await container.getFloorPlanSheetFile.execute(
      { projectId, sheetId: prancha.id },
      session,
    );
    if (!url.ok) return undefined;
    const renderizada = await renderizar(url.value);
    const metros = metresPerPixelAt(calibracao, renderizada.width);
    return {
      image: renderizada.image,
      width: renderizada.width * metros,
      height: renderizada.height * metros,
    };
  })().catch(() => undefined);
  renderizadas.set(chave, pagina);
  const pronta = await pagina;
  // A que falhou não fica guardada: a próxima abertura tenta de novo.
  if (pronta === undefined) renderizadas.delete(chave);
  return pronta;
}

export function usePranchaDaFrente(floorPlanId: string | undefined): FundoDaPlanta | undefined {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  return useLive(
    () =>
      floorPlanId === undefined || session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : lerPranchaDaFrente(container, session, activeProjectId, floorPlanId),
    [floorPlanId, session?.userId, activeProjectId],
  );
}
